import axios from 'axios';

import { metersBetween, predictionDistance, rankByDistance } from './placeDistance';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

export const PLACES_MIN_QUERY_LENGTH = 3;

export type PlaceSuggestion = {
  value: string;
  label: string;
  /** Straight-line distance from `near`, when one was given. */
  distanceMeters?: number;
};

/** Google's largest bias circle. */
const BIAS_RADIUS_M = 50_000;

type Prediction = {
  placePrediction?: { text?: { text?: string }; distanceMeters?: number; types?: string[] };
};

/** `near` biases the results towards a point and returns each one's distance from it, nearest tiers first. */
export const searchPlaces = async (
  query: string,
  signal?: AbortSignal,
  near?: { latitude: number; longitude: number },
): Promise<PlaceSuggestion[]> => {
  const input = query.trim();
  if (!GOOGLE_MAPS_API_KEY || input.length < PLACES_MIN_QUERY_LENGTH) return [];

  try {
    const { data } = await axios.post(
      'https://places.googleapis.com/v1/places:autocomplete',
      near
        ? { input, origin: near, locationBias: { circle: { center: near, radius: BIAS_RADIUS_M } } }
        : { input },
      {
        signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask':
            'suggestions.placePrediction.text,suggestions.placePrediction.distanceMeters,suggestions.placePrediction.types',
        },
      },
    );

    const seen = new Set<string>();

    const places = ((data?.suggestions ?? []) as Prediction[]).flatMap(
      ({ placePrediction }): PlaceSuggestion[] => {
        const text = placePrediction?.text?.text;
        if (!text || seen.has(text)) return [];
        seen.add(text);
        return [
          {
            value: text,
            label: text,
            distanceMeters: predictionDistance(placePrediction ?? {}, !!near),
          },
        ];
      },
    );

    return rankByDistance(places);
  } catch {
    return [];
  }
};

export type NearbyPlace = PlaceSuggestion & {
  distanceMeters: number;
  /** Google's own label: "Marina", "Yacht Club". */
  typeLabel?: string;
  city?: string;
  rating?: number;
};

type NearbyResult = {
  displayName?: { text?: string };
  formattedAddress?: string;
  shortFormattedAddress?: string;
  location?: { latitude: number; longitude: number };
  googleMapsTypeLabel?: { text?: string };
  rating?: number;
};

type NearbyHit = Omit<NearbyPlace, 'distanceMeters'> & {
  location: { latitude: number; longitude: number };
};

// ponytail: kept per ~1 km square for the session, so going back and forth through the wizard
// costs one call, even when each visit's phone fix lands a few metres from the last. Distances
// are measured from each caller's own point. A failed call is dropped, so the next visit tries again.
const nearbyCache = new Map<string, Promise<NearbyHit[]>>();

/**
 * The most popular marinas within 50 km of `near`, for the empty location box. Ranked by
 * popularity, not distance: by distance, a dense coast fills all 20 slots with boat clubs and
 * repair shops inside 3 nm. `rating` puts the call on Google's Nearby Search Enterprise SKU.
 */
export const searchNearbyMarinas = async (near: {
  latitude: number;
  longitude: number;
}): Promise<NearbyPlace[]> => {
  if (!GOOGLE_MAPS_API_KEY) return [];
  const key = `${near.latitude.toFixed(2)},${near.longitude.toFixed(2)}`;
  const hits = await (nearbyCache.get(key) ?? fetchNearbyMarinas(near, key));
  return hits.map(({ location, ...hit }) => ({
    ...hit,
    distanceMeters: metersBetween(near, location),
  }));
};

const fetchNearbyMarinas = (near: { latitude: number; longitude: number }, key: string) => {
  const request = axios
    .post(
      'https://places.googleapis.com/v1/places:searchNearby',
      {
        includedPrimaryTypes: ['marina'],
        rankPreference: 'POPULARITY',
        locationRestriction: { circle: { center: near, radius: BIAS_RADIUS_M } },
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask':
            'places.displayName,places.formattedAddress,places.shortFormattedAddress,places.location,places.googleMapsTypeLabel,places.rating',
        },
      },
    )
    .then(({ data }) =>
      ((data?.places ?? []) as NearbyResult[]).flatMap((place): NearbyHit[] => {
        const name = place.displayName?.text;
        if (!name || !place.location) return [];
        return [
          {
            value: place.formattedAddress ? `${name}, ${place.formattedAddress}` : name,
            label: name,
            location: place.location,
            typeLabel: place.googleMapsTypeLabel?.text,
            city: place.shortFormattedAddress?.split(', ').at(-1),
            rating: place.rating,
          },
        ];
      }),
    )
    .catch((): NearbyHit[] => {
      nearbyCache.delete(key);
      return [];
    });

  nearbyCache.set(key, request);
  return request;
};
